// Utility functions for transforming contact data between legacy and array formats
import { DEFAULT_EMAIL_ENTRY, DEFAULT_PHONE_ENTRY } from './constants';

/**
 * Transform adult data from legacy format to array format
 * Used when loading initial data with feature flag enabled
 */
export const transformToArrayFormat = (adult) => {
  if (!adult) return adult;

  // If already has arrays, return as-is
  if (adult.phones || adult.emails) {
    return adult;
  }
  const transformed = { ...adult };

  const contactPhones = adult._contactData?.phones;
  const contactEmails = adult._contactData?.emails;
  const defaultEmailType = DEFAULT_EMAIL_ENTRY.emailType;
  const defaultPhoneType = DEFAULT_PHONE_ENTRY.phoneType;

  if (contactPhones && contactPhones.length > 0) {
    transformed.phones = contactPhones
      .map((phone) => ({
        phoneNumber: phone.phoneNumber || phone.phoneNumbe, // Handle PostgreSQL 63-char limit truncation
        phoneType: phone.phoneType,
        isPrimary: phone.isPrimary,
      }))
      .sort((a, b) => (b.isPrimary ? 1 : 0) - (a.isPrimary ? 1 : 0)); // Primary first
  } else if (adult.phone) {
    transformed.phones = [
      {
        phoneNumber: adult.phone,
        phoneType: defaultPhoneType,
        isPrimary: true,
      },
    ];
  } else {
    transformed.phones = [
      {
        phoneNumber: '',
        phoneType: defaultPhoneType,
        isPrimary: true,
      },
    ];
  }

  if (contactEmails && contactEmails.length > 0) {
    transformed.emails = contactEmails
      .map((email) => ({
        emailAddress: email.emailAddress || email.emailAddre, // Handle PostgreSQL 63-char limit truncation
        emailType: email.emailType,
        isPrimary: email.isPrimary,
      }))
      .sort((a, b) => (b.isPrimary ? 1 : 0) - (a.isPrimary ? 1 : 0)); // Primary first
  } else if (adult.email) {
    transformed.emails = [
      {
        emailAddress: adult.email,
        emailType: defaultEmailType,
        isPrimary: true,
      },
    ];
  } else {
    transformed.emails = [
      {
        emailAddress: '',
        emailType: defaultEmailType, // Default type
        isPrimary: true,
      },
    ];
  }

  return transformed;
};

/**
 * Transform adult data from array format back to legacy format
 * Used when submitting data to maintain backward compatibility
 */
export const transformToLegacyFormat = (adult) => {
  if (!adult) return adult;

  const transformed = { ...adult };

  // Sync primary phone with legacy phone field
  if (adult.phones && adult.phones.length > 0) {
    const primaryPhone = adult.phones.find((p) => p.isPrimary);
    transformed.phone = primaryPhone
      ? primaryPhone.phoneNumber
      : adult.phones[0].phoneNumber;
  }

  // Sync primary email with legacy email field
  if (adult.emails && adult.emails.length > 0) {
    const primaryEmail = adult.emails.find((e) => e.isPrimary);
    transformed.email = primaryEmail
      ? primaryEmail.emailAddress
      : adult.emails[0].emailAddress;
  }

  return transformed;
};

/**
 * Clean contact arrays by removing empty entries
 */
export const cleanContactArrays = (adult) => {
  if (!adult) return adult;

  const cleaned = { ...adult };

  // Clean phones array
  if (cleaned.phones) {
    cleaned.phones = cleaned.phones.filter(
      (phone) => phone.phoneNumber && phone.phoneNumber.trim() !== '',
    );
    // Ensure first phone is primary if any exist
    if (cleaned.phones.length > 0 && !cleaned.phones.some((p) => p.isPrimary)) {
      cleaned.phones[0].isPrimary = true;
    }
  }

  // Clean emails array
  if (cleaned.emails) {
    cleaned.emails = cleaned.emails.filter(
      (email) => email.emailAddress && email.emailAddress.trim() !== '',
    );
    // Ensure first email is primary if any exist
    if (cleaned.emails.length > 0 && !cleaned.emails.some((e) => e.isPrimary)) {
      cleaned.emails[0].isPrimary = true;
    }
  }

  return cleaned;
};

/**
 * Ensure contact data integrity
 * Called before form submission
 */
export const ensureContactDataIntegrity = (adult) => {
  if (!adult) return adult;

  let processed = cleanContactArrays(adult);
  processed = transformToLegacyFormat(processed);

  // Remove empty arrays to save bandwidth but keep populated ones
  // This sends both legacy fields (phone, email) and new arrays (phones[], emails[])
  if (processed.phones && processed.phones.length === 0) {
    delete processed.phones;
  }
  if (processed.emails && processed.emails.length === 0) {
    delete processed.emails;
  }

  // Remove the temporary _contactData field used for transformation
  delete processed._contactData;

  return processed;
};
